// Frontend mirror of supabase/functions/_shared/llm-presets.ts — keep in sync.
export type PresetKey =
  | "groq" | "cerebras" | "gemini" | "mistral" | "openrouter" | "ollama"
  | "openai" | "anthropic_compat" | "humain" | "vllm_selfhosted" | "custom";

export type ModelTag = "free" | "light" | "preview";
export interface ModelOption { id: string; label: string; tags: ModelTag[]; note?: string }

export interface PresetDef {
  label: string;
  baseUrl: string;        // "" = user must supply
  suggestedModel: string; // "" = user must supply
  keyless: boolean;       // true = api key optional (local endpoints)
  /** Curated model suggestions. Checked against each provider's own docs in Oct 2026; the
   *  dialog's "Load models" button reads the provider's live /models list, which is the
   *  source of truth when the two disagree. */
  models: ModelOption[];
  /** Free-tier summary shown under the picker. */
  freeNote?: string;
}

export const LLM_PRESETS: Record<PresetKey, PresetDef> = {
  groq: {
    label: "Groq",
    baseUrl: "https://api.groq.com/openai/v1",
    suggestedModel: "openai/gpt-oss-20b",
    keyless: false,
    freeNote: "Free plan: about 30 requests/min, 1,000 requests/day and 200K tokens/day per model.",
    models: [
      { id: "openai/gpt-oss-20b", label: "GPT-OSS 20B", tags: ["free", "light"], note: "Fast and small; best default" },
      { id: "openai/gpt-oss-120b", label: "GPT-OSS 120B", tags: ["free"], note: "Strongest free option" },
      { id: "qwen/qwen3.8-27b", label: "Qwen3.8 27B", tags: ["free", "preview"], note: "Preview model" },
    ],
  },
  cerebras: {
    label: "Cerebras",
    baseUrl: "https://api.cerebras.ai/v1",
    suggestedModel: "qwen-3.8-27b",
    keyless: false,
    freeNote: "Free trial tier with rate limits; very fast inference.",
    models: [
      { id: "qwen-3.8-27b", label: "Qwen 3.8 27B", tags: ["free", "light"], note: "64K context on free tier" },
      { id: "gpt-oss-120b", label: "GPT-OSS 120B", tags: ["free"], note: "65K context on free tier" },
    ],
  },
  gemini: {
    label: "Google Gemini",
    baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai",
    suggestedModel: "gemini-2.5-flash-lite",
    keyless: false,
    freeNote: "Free tier in Google AI Studio with rate limits; data may be used to improve Google products on the free tier.",
    models: [
      { id: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash-Lite", tags: ["free", "light"], note: "Smallest, cheapest Gemini" },
      { id: "gemini-3.5-flash-lite", label: "Gemini 3.5 Flash-Lite", tags: ["free", "light"] },
      { id: "gemini-3.1-flash-lite", label: "Gemini 3.1 Flash-Lite", tags: ["free", "light"] },
      { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash", tags: ["free"] },
      { id: "gemini-3.8-flash", label: "Gemini 3.8 Flash", tags: ["free"], note: "Newest Flash" },
    ],
  },
  mistral: {
    label: "Mistral",
    baseUrl: "https://api.mistral.ai/v1",
    suggestedModel: "ministral-3-8b-25-12",
    keyless: false,
    freeNote: "Free 'Experiment' plan on La Plateforme (rate limited, requires phone verification).",
    models: [
      { id: "ministral-3-3b-25-12", label: "Ministral 3 3B", tags: ["free", "light"], note: "Smallest" },
      { id: "ministral-3-8b-25-12", label: "Ministral 3 8B", tags: ["free", "light"] },
      { id: "ministral-3-14b-25-12", label: "Ministral 3 14B", tags: ["free", "light"] },
      { id: "mistral-small-4-0-26-03", label: "Mistral Small 4", tags: ["free"] },
    ],
  },
  openrouter: {
    label: "OpenRouter",
    baseUrl: "https://openrouter.ai/api/v1",
    suggestedModel: "",
    keyless: false,
    freeNote: "Many models are free (IDs end in ':free'). Click Load models to list them live.",
    models: [],
  },
  ollama: {
    label: "Ollama (local, free)",
    baseUrl: "http://host.docker.internal:11434/v1",
    suggestedModel: "llama3.2:3b",
    keyless: true,
    freeNote: "Runs on your own machine at no cost. Pull a model first (ollama pull <name>), then click Load models.",
    models: [
      { id: "llama3.2:3b", label: "Llama 3.2 3B", tags: ["free", "light"] },
      { id: "gemma3:4b", label: "Gemma 3 4B", tags: ["free", "light"] },
      { id: "qwen3:4b", label: "Qwen3 4B", tags: ["free", "light"] },
    ],
  },
  openai: {
    label: "OpenAI",
    baseUrl: "https://api.openai.com/v1",
    suggestedModel: "",
    keyless: false,
    freeNote: "No free API tier. Click Load models to see the models your key can use.",
    models: [],
  },
  anthropic_compat: {
    label: "Anthropic (OpenAI-compatible proxy)",
    baseUrl: "",
    suggestedModel: "",
    keyless: false,
    models: [],
  },
  humain: {
    label: "HUMAIN",
    baseUrl: "https://api.node.humain.com/v1",
    suggestedModel: "humain-m3-preview",
    keyless: false,
    models: [{ id: "humain-m3-preview", label: "HUMAIN M3 (preview)", tags: ["preview"] }],
  },
  vllm_selfhosted: {
    label: "Self-hosted vLLM",
    baseUrl: "",
    suggestedModel: "",
    keyless: true,
    models: [],
  },
  custom: {
    label: "Custom OpenAI-compatible",
    baseUrl: "",
    suggestedModel: "",
    keyless: false,
    models: [],
  },
};

export function isPresetKey(v: unknown): v is PresetKey {
  return typeof v === "string" && v in LLM_PRESETS;
}
